import React from 'react';
import PropTypes from 'prop-types';
import Spinner from './Spinner';
import './LoadingSystem.css';

/**
 * LoadingButton - Botón con estado de carga integrado
 * 
 * Proporciona feedback visual inmediato al usuario durante operaciones asíncronas
 * siguiendo las mejores prácticas de UX.
 * 
 * Casos de uso:
 * - Botones de envío de formularios
 * - Acciones que requieren llamadas a la API
 * - Operaciones de guardado o eliminación
 * 
 * Beneficios:
 * - Previene múltiples clicks durante la carga
 * - Mantiene el tamaño del botón durante el loading
 * - Proporciona feedback visual claro
 * 
 * @example
 * <LoadingButton
 *   loading={isSubmitting}
 *   onClick={handleSubmit}
 *   variant="primary"
 * >
 *   Guardar Cambios
 * </LoadingButton>
 */
const LoadingButton = ({ 
  children,
  loading = false,
  disabled = false,
  onClick,
  variant = 'primary',
  size = 'medium',
  type = 'button',
  fullWidth = false,
  icon = null,
  loadingText = null,
  className = '',
  ...rest
}) => {
  const buttonClass = `
    loading-button
    loading-button--${variant}
    loading-button--${size}
    ${fullWidth ? 'loading-button--full-width' : ''}
    ${loading ? 'loading-button--loading' : ''}
    ${className}
  `.trim();

  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      className={buttonClass}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={loading}
      {...rest}
    >
      {loading ? (
        <>
          <Spinner size="small" color={variant === 'primary' ? 'white' : 'primary'} />
          <span className="loading-button__text">
            {loadingText || children}
          </span>
        </>
      ) : (
        <>
          {icon && <span className="loading-button__icon">{icon}</span>}
          <span className="loading-button__text">{children}</span>
        </>
      )}
    </button>
  );
};

LoadingButton.propTypes = {
  /** Contenido del botón */
  children: PropTypes.node.isRequired,
  /** Estado de carga del botón */
  loading: PropTypes.bool,
  /** Estado deshabilitado del botón */
  disabled: PropTypes.bool,
  /** Función que se ejecuta al hacer click */
  onClick: PropTypes.func,
  /** Variante visual del botón */
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'outline']),
  /** Tamaño del botón */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Tipo de botón HTML */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Botón ocupa todo el ancho disponible */
  fullWidth: PropTypes.bool,
  /** Icono opcional a mostrar antes del texto */
  icon: PropTypes.node,
  /** Texto alternativo a mostrar durante la carga */
  loadingText: PropTypes.string,
  /** Clase CSS adicional */
  className: PropTypes.string,
};

export default LoadingButton;
